import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { Versioned } from '@adornis/versioned-entity/db/versioned-mongo-entity.js';
import type { Weekday } from '@chancenwerk/db/enums/enums.js';
import { KPF } from '@chancenwerk/master-data-management/kpf/entities/KPF.js';
import { Mitarbeiter } from '@chancenwerk/master-data-management/users/entities/Mitarbeiter.js';
import { WorkshopAPI } from '@chancenwerk/workshops/api/WorkshopAPI.js';
import { Workshop } from '@chancenwerk/workshops/entities/Workshop.js';
import { WorkshopAttendanceStatus } from '@chancenwerk/workshops/entities/WorkshopAttendanceStatus.js';
import { WorkshopFilter } from '@chancenwerk/workshops/entities/WorkshopFilter.js';
import { DateTime } from 'luxon';
import { firstValueFrom } from 'rxjs';

export enum CapacityRequestType {
  LEKO = 'LeKo',
  SCHUKO = 'SchuKo',
}
export enum CapacityRequestStatus {
  NEW = 'neu',
  IN_PROGRESS = 'in Arbeit',
  TRIAL_DAY = 'Schnuppertag',
  HIRED = 'eingestellt',
}

@Versioned({
  customName: 'Bedarfsanfrage',
})
@Entity()
export class CapacityRequest extends MongoEntity {
  static override _class = 'CapacityRequest';
  static override _collectionName = 'capacity-requests';

  @Field(type => Boolean)
  public highPriority: Maybe<boolean>;

  @validate(nonOptional())
  @Field(type => String)
  public type: Maybe<CapacityRequestType>;

  @validate(nonOptional())
  @Field(type => DateTime)
  public fromWhen: Maybe<DateTime>;

  @validate(nonOptional())
  @Field(type => [String])
  public weekdays: Maybe<Weekday[]>;

  @validate(nonOptional())
  @Field(type => String)
  public status: Maybe<CapacityRequestStatus>;

  @Field(type => String)
  public specialSkills: Maybe<string>;

  @Field(type => String)
  public comment: Maybe<string>;

  @Field(type => String)
  public vorname: Maybe<string>;

  @Field(type => String)
  public nachname: Maybe<string>;

  @Field(type => String)
  public telefonBewerber: Maybe<string>;

  @Field(type => String)
  public bewerbungslink: Maybe<string>;

  @Field(type => DateTime)
  public schnuppertagDatum: Maybe<DateTime>;

  @Field(type => DateTime)
  public datumVersandWillkommensmail: Maybe<DateTime>;

  @Field(type => DateTime)
  public datumErsteTeilnahmeLernfoerderung: Maybe<DateTime>;

  @Field(type => DateTime)
  public datumFeedbackunterlagenErhalten: Maybe<DateTime>;

  @Field(type => DateTime)
  public datumFeedbackgespraech: Maybe<DateTime>;

  @Field(type => DateTime, { default: v => v ?? DateTime.now() })
  public createdAt: Maybe<DateTime>;

  @validate(nonOptional())
  @Field(type => String)
  public createdByID: Maybe<string>;

  @DataLoadedField(type => Mitarbeiter, ({ createdByID }) => createdByID)
  public createdBy: Maybe<Mitarbeiter>;

  @Field(type => String)
  public editedByID: Maybe<string>;

  @DataLoadedField(type => Mitarbeiter, ({ editedByID }) => editedByID)
  public editedBy: Maybe<Mitarbeiter>;

  @Field(type => DateTime, { default: v => v ?? DateTime.now() })
  public editedAt: Maybe<DateTime>;

  @validate(nonOptional())
  @Field(type => String)
  public KPFID: Maybe<string>;

  @DataLoadedField(type => KPF, ({ KPFID }) => KPFID)
  public KPF: Maybe<KPF>;

  @Field(type => String)
  public linkedUserID: Maybe<string>;

  @DataLoadedField(type => Mitarbeiter, ({ linkedUserID }) => linkedUserID)
  public linkedUser: Maybe<Mitarbeiter>;

  @Field(type => DateTime, {
    resolve: function (this: CapacityRequest) {
      return async () => {
        if (!this.linkedUserID) {
          return null;
        }

        const workshopID = (
          await loadMongoData(Workshop, {
            title: { $regex: 'chancenspirit', $options: 'i' },
          })
        )[0]?._id;
        if (!workshopID) {
          return null;
        }

        const attend = await firstValueFrom(
          WorkshopAPI.subscribeAllAttendanceSimple(
            0,
            1,
            new WorkshopFilter({
              userID: this.linkedUserID,
              workshopID,
              status: WorkshopAttendanceStatus.ATTENDED,
            }),
          )({ date: 1 }),
        );

        return attend[0]?.date;
      };
    },
  })
  public datumChancenSpirit: Maybe<DateTime>;

  @Field(type => DateTime, {
    resolve: function (this: CapacityRequest) {
      return async () => {
        if (!this.linkedUserID) {
          return null;
        }

        const workshopID = (
          await loadMongoData(Workshop, {
            title: { $regex: 'einführung lernkaskade', $options: 'i' },
          })
        )[0]?._id;
        if (!workshopID) {
          return null;
        }

        const attend = await firstValueFrom(
          WorkshopAPI.subscribeAllAttendanceSimple(
            0,
            1,
            new WorkshopFilter({
              userID: this.linkedUserID,
              workshopID,
              status: WorkshopAttendanceStatus.ATTENDED,
            }),
          )({ date: 1 }),
        );

        return attend[0]?.date;
      };
    },
  })
  public datumEinfuehrungLKK: Maybe<DateTime>;
}
